import { Button, FloatButton } from 'antd';
import React from 'react';
import * as C from 'src/pages/indicators/EditIndicatorPage/styles';
import DashboardTemplate from 'src/templates/DashboardTemplate';
import {
	AppstoreAddOutlined,
	CodeOutlined,
	CopyOutlined,
	DeleteOutlined,
	DoubleRightOutlined,
	EllipsisOutlined,
	PlusOutlined,
	SaveOutlined,
	TableOutlined
} from '@ant-design/icons';
import { CustomEditor } from 'src/pages/indicators/EditIndicatorPage/_components/CustomEditor';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import RenderViewFromTemplate from 'src/components/RenderViewFromTemplate';
import {
	useGetCustomDashboardByIdQuery,
	useGetCustomDashboardObserversQuery,
	useGetObserversQuery,
	usePatchCustomDashboardMutation
} from 'src/redux/services/centralApi';
import { useParams } from 'react-router-dom';
import { IDashboardTree } from 'src/pages/indicators/EditIndicatorPage/types/dashboardTree';
import { IQueryFieldsItem } from 'src/interfaces/entities/observer';
import EditorPagination, {
	IEditorPaginationProps
} from 'src/pages/indicators/EditIndicatorPage/_components/EditorPagination';
import ComponentsStore from 'src/pages/indicators/EditIndicatorPage/_components/ComponentsStore';
import usePersistOnLocalStorage from 'src/hooks/usePersistOnLocalStorage';
import { toast } from 'react-toastify';
import EditableTables from 'src/pages/indicators/EditIndicatorPage/_components/EditableTables';

export default function EditIndicatorPage() {
	const dashboard_id = useParams()?.dashboard_id ?? '';
	const [ patchCustomDashboard, ] = usePatchCustomDashboardMutation();
	const { data: observersData, } =
        useGetObserversQuery();
	const { data: customDashboardObserversData, } =
        useGetCustomDashboardObserversQuery({ dashboard_id, });
	const { data: dashboardData, isLoading: isLoadingDashboardData, } =
        useGetCustomDashboardByIdQuery({ dashboard_id, });
	const [ layout, setLayout, ] = React.useState<(GridLayout.Layout & { template: string })[]>([]);
	const [ bottomContainerOpen, setBottomContainerOpen, ] = React.useState(true);
	const [ selectedTab, setSelectedTab, ] = React.useState('biblioteca');
	const [ editingTemplate, setEditingTemplate, ] = React.useState('');
	const [ dashboardTree, setDashboardTree, ] = React.useState<IDashboardTree>(
		{}
	);
	const {
		getData: getLastSavedTemplate,
		getTimestamp: getLastSavedTemplateTimestamp,
		removeData: removeLastSavedTemplate,
	} = usePersistOnLocalStorage({
		key: dashboard_id,
		data: layout,
		notPersistWhen:
            !dashboard_id ||
            dashboardData?.layout == layout ||
            layout.length == 0,
	});
	
	const [ sqlQueries, setSqlQueries, ] = React.useState<{
		[key: string]: IQueryFieldsItem[];
	}>({});

	const tabs: IEditorPaginationProps['tabs'] = [
		{ content: <AppstoreAddOutlined />, id: 'biblioteca', },
		{ content: <CodeOutlined />, id: 'editor', },
		{ content: <TableOutlined />, id: 'table', },
	];

	
	const customDashboardObservers = React.useMemo(() => {
		const customDashboardObserversIds =
            customDashboardObserversData?.map((i) => i.observer_id) ?? [];
		return (
			observersData?.filter((observer) =>
				customDashboardObserversIds.includes(observer.observer_id)
			) ?? []
		);
	}, [ observersData, customDashboardObserversData, ]);


	const handleTemplateGeneration = (template: string) => {
		setLayout([
			...layout,
			{
				i: window.crypto.randomUUID(),
				x: 0,
				y: 0,
				w: 4,
				h: 4,
				template,
			},
		]);
	};

	React.useEffect(() => {
		const lastSavedLayout = getLastSavedTemplate();

		const lastSavedPositions = lastSavedLayout?.map((item) => {
			const { x, y, w, h, i, moved, } = item;
			return { x, y, w, h, i, moved, };
		});

		const parsedDashboardDataLayout = typeof dashboardData?.layout == 'string' 
			?	(JSON.parse(dashboardData?.layout ?? '[]') as (GridLayout.Layout & { template: string })[]) 
			: dashboardData?.layout ?? [];

		const dashboardDataLayoutPositions = parsedDashboardDataLayout?.map(
			(item) => {
				const { x, y, w, h, i, moved, } = item;
				return { x, y, w, h, i, moved, };
			}
		);

		if (
			!isLoadingDashboardData &&
            JSON.stringify(lastSavedPositions) !=
                JSON.stringify(dashboardDataLayoutPositions) &&
            !!lastSavedLayout?.length
		) {
			const timestamp = getLastSavedTemplateTimestamp();
			setLayout(lastSavedLayout);

			toast.info(
				`Última alteração salva (localmente) em ${new Date(
					timestamp ?? 0
				).toLocaleString()}`
			);
		} else {
			setLayout(parsedDashboardDataLayout);
		}
	}, [ dashboardData, ]);

	const dashboardContent = layout.map((item) => (
		<C.Widget
			style={{
				border: `1px solid rgba(0,0,0,.2)`,
				boxShadow:
					' 10px 10px 30px -10px rgba(191,191,191,.7)',
			}}
			key={item.i}
		>
			<RenderViewFromTemplate
				templateProxy={item.template}
				viewProxy={dashboardTree}
			/>
			<C.WidgetMenu
				trigger={[ 'hover', ]}
				placement='bottomRight'
				menu={{
					items: [
						{
							key: '2',
							label: <>Copiar para o editor</>,
							icon: <CopyOutlined />,
							onClick: () => {
								setEditingTemplate(
									layout.find(
										(i) => i.i === item.i
									)?.template ?? ''
								);
							},
						},
						{
							key: '1',
							label: <>Deletar</>,
							icon: <DeleteOutlined />,
							danger: true,
							onClick: () => {
								setLayout(
									layout.filter(
										(i) => i.i !== item.i
									)
								);
							},
						},
					],
				}}
			>
				<Button
					type='text'
					style={{
						width: 'fit-content',
						padding: 3,
						height: 'fit-content',
					}}
					icon={<EllipsisOutlined />}
				/>
			</C.WidgetMenu>
		</C.Widget>
	));

	React.useEffect(() => {
		const queries: {
			[key: string]: IQueryFieldsItem[];
		} = {};
		
		customDashboardObservers.forEach((observer) => {
			const observerNameAsVariable = observer.short_name
				?.replace(/ /g, '_')
				.toLowerCase();
			const parsedSqlTestResult = (
				typeof observer.sql_test_result === 'string'
					? JSON.parse(observer.sql_test_result)
					: observer.sql_test_result
			) as IQueryFieldsItem[];
			queries[observerNameAsVariable] = parsedSqlTestResult;
		});
		setSqlQueries(queries);
	}, [ customDashboardObservers, ]);

	React.useEffect(() => {
		const newPreviewItem = layout.find((i) => i.i === 'preview');
		if (newPreviewItem) {
			newPreviewItem.template = editingTemplate;
			setLayout([
				...layout.filter((i) => i.i !== 'preview'),
				newPreviewItem,
			]);
		}
	}, [ editingTemplate, ]);


	return (
		<DashboardTemplate totallyColapseSidebar contentOnly>
			<GridLayout
				onLayoutChange={(_layout) => {
					const newLayout = _layout.map((item) => {
						return {
							...item,
							template:
                                layout.find((i) => i.i === item.i)?.template ??
                                '',
						};
					});
					setLayout(newLayout);
				}}
				className='layout'
				layout={layout}
				cols={30}
				rowHeight={30}
				margin={[ 0, 0, ]}
				width={window.innerWidth}
				style={{
					overflow: 'hidden',
				}}
			>
			
				{dashboardContent}
			</GridLayout>
			<C.BottomContainer justify='start' open={bottomContainerOpen}>
				<EditorPagination
					containerStyles={{
						position: 'absolute',
						top: '-20px',
						left: '200px',
					}}
					tabs={tabs}
					onChange={(val) => setSelectedTab(String(val))}
				/>
				<C.BottomContainerTrigger
					title='Abrir/Fechar editor'
					onClick={() => {
						setBottomContainerOpen((prev) => !prev);
					}}
					open={bottomContainerOpen}
				>
					<DoubleRightOutlined />
				</C.BottomContainerTrigger>
				{selectedTab == 'editor' && (
					<>
						<C.EditorsContainer>
							{!!Object.entries(sqlQueries)?.length && (
								<CustomEditor
									dashboardTree={dashboardTree}
									onChange={(val) => {
										setEditingTemplate(val);
									}}
									value={editingTemplate}
									sqlQueries={sqlQueries}
								/>
							)}
						</C.EditorsContainer>
						<C.BottomContainerActions>
							<C.PreviewContainer>
								<RenderViewFromTemplate
									templateProxy={editingTemplate}
									viewProxy={dashboardTree}
								/>
							</C.PreviewContainer>
							<Button
								style={{
									width: '100%',
									height: '100%',
									borderRadius: 0,
								}}
								title='Adicionar à grid'
								icon={<PlusOutlined />}
								type='primary'
								disabled={!editingTemplate}
								onClick={() => {
									handleTemplateGeneration(editingTemplate);
								}}
							/>
						</C.BottomContainerActions>
					</>
				)}
				{selectedTab == 'biblioteca' && (
					<ComponentsStore
						onTemplateGenerated={handleTemplateGeneration}
						dashboardTree={dashboardTree}
					/>
				)}

				<div
					style={{
						display: selectedTab == 'table' ? 'flex' : 'none',
						width: '100%',
						height: '100%',
					}}
				>
					<EditableTables
						observers={customDashboardObservers ?? []}
						onChange={(val) => {
							setDashboardTree(val as unknown as IDashboardTree);
						}}
					/>
				</div>
			</C.BottomContainer>
			<FloatButton
				icon={<SaveOutlined />}
				onClick={() => {
					patchCustomDashboard({
						dashboard_id,
						body: {
							layout: JSON.stringify(layout),
						},
					}).then(() => {
						toast.success('Alterações salvas com sucesso!');
						removeLastSavedTemplate();
					});
				}}
				tooltip='Enviar alterações'
			/>
		</DashboardTemplate>
	);
}
